import { useEffect, useState, type ComponentType } from 'react';
import { useParams } from 'react-router-dom';
import { useWordPressPage } from '../hooks/useWordPress';
import { blogSlug } from '../config';
import { PageLoader } from './PageLoader';
import { Seo } from './Seo';
import { toPlainText } from '../utils/html';
import { SiteImage } from './site/SiteImage';
import { IMAGES } from '../content/images';

/**
 * Local component mapping for dynamic imports.
 * Known local routes render instantly — no WP API call needed.
 */
const localComponents: { [key: string]: () => Promise<any> } = {
  home: () => import('../pages/Home'),
  about: () => import('../pages/About'),
  programs: () => import('../pages/Programs'),
  contact: () => import('../pages/Contact'),
  store: () => import('../pages/Donate'), // Donate page (the dormant ecommerce module no longer owns /store)
  'get-help': () => import('../pages/GetHelp'),
  privacy: () => import('../pages/Privacy'),
  [blogSlug]: () => import('../pages/Blog'),
};

const hasLocalComponent = (slug: string) => slug in localComponents;

/**
 * Dynamic Page Component
 *
 * Handles both WordPress CMS pages and local React components.
 * Strategy: local-first for known routes, WP-first for unknown slugs.
 * 1. If slug has a local component → render it immediately (skip WP)
 * 2. Otherwise fetch WordPress page by slug
 * 3. Show 404 if neither exists
 */
const DynamicPage = () => {
  const { slug = 'home' } = useParams<{ slug: string }>();
  const isLocal = hasLocalComponent(slug);

  // Only fire the WP query for unknown slugs
  const { data: wpPage, isLoading: wpLoading } = useWordPressPage(slug, { enabled: !isLocal });

  const [LocalComponent, setLocalComponent] = useState<ComponentType | null>(null);
  const [localLoading, setLocalLoading] = useState(isLocal);

  // Load local component immediately for known routes
  useEffect(() => {
    setLocalComponent(null);
    setLocalLoading(isLocal);

    if (!isLocal) return;

    localComponents[slug]()
      .then(mod => {
        setLocalComponent(() => mod.default);
        setLocalLoading(false);
      })
      .catch(() => {
        setLocalLoading(false);
      });
  }, [slug, isLocal]);

  // Local route: wait for dynamic import only
  if (isLocal) {
    if (localLoading) return <PageLoader />;
    if (LocalComponent) return <LocalComponent />;
  }

  // WP route: wait for API response
  if (wpLoading) return <PageLoader />;

  if (wpPage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Seo
          title={toPlainText(wpPage.title.rendered)}
          path={`/${slug}`}
          description={toPlainText(wpPage.excerpt?.rendered ?? '', 200) || undefined}
        />
        <article className="max-w-4xl mx-auto">
          <h1
            className="text-4xl font-bold mb-8"
            dangerouslySetInnerHTML={{ __html: wpPage.title.rendered }}
          />
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: wpPage.content.rendered }}
          />
        </article>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center">
        <Seo title="Page not found" path={`/${slug}`} noindex />
        <SiteImage image={IMAGES['ILL-03']} sizes="200px" loading="eager" className="w-48 h-48 object-contain mx-auto mb-6" />
        <h1 className="text-4xl font-bold mb-4">Page Not Found</h1>
        <p className="text-body mb-4">The page you're looking for doesn't exist.</p>
        <a href="/" className="text-primary hover:text-primary-dark">
          Return to Home
        </a>
      </div>
    </div>
  );
};

export default DynamicPage; 